import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  Users,
  Trophy,
  Clock,
  ArrowLeft,
  User,
  CheckCircle2,
  AlertCircle,
  FileText,
  Share2,
  Swords,
  Ban,
} from 'lucide-react';
import { format } from 'date-fns';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  sportGradient,
  statusStyle,
  capitalize,
  formatLabel,
  formatFee,
  countdownLabel,
} from '../../utils/tournamentUi';

interface Tournament {
  _id: string;
  title: string;
  sport: string;
  description: string;
  rules: string[];
  location: string;
  venue?: string;
  date: string;
  time: string;
  endDate?: string;
  registrationDeadline?: string;
  maxParticipants: number;
  participantCount: number;
  availableSpots: number;
  registrationFee: number;
  prizePool?: string;
  prizes: Array<{ position: string; prize: string }>;
  organizer?: string;
  bannerImage?: string;
  brochure?: string;
  status: string;
  format?: string;
  registrationOpen: boolean;
  isRegistered?: boolean;
  createdBy?: {
    name: string;
    avatar?: string;
  };
}

const safeFormat = (value: string | undefined, pattern: string) => {
  if (!value) return '';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '' : format(d, pattern);
};

const TournamentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);
  const [justRegistered, setJustRegistered] = useState(false);

  const fetchTournament = useCallback(async () => {
    try {
      const res = await api.get(`/tournaments/${id}`);
      if (res.data.success) {
        setTournament(res.data.data);
        setError(null);
      } else {
        setError('Tournament not found');
      }
    } catch (err) {
      setError('This tournament could not be loaded.');
      console.error('Error fetching tournament:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      fetchTournament();
    }
  }, [id, fetchTournament]);

  const alreadyRegistered = justRegistered || Boolean(user && tournament?.isRegistered);

  const handleRegister = async () => {
    if (!tournament || registering || alreadyRegistered) return;
    if (!user) {
      toast('Please log in to register');
      navigate('/login');
      return;
    }
    try {
      setRegistering(true);
      await api.post(`/tournaments/${id}/register`);
      setJustRegistered(true);
      toast.success('You are registered for this tournament!');
      fetchTournament();
    } catch (err: any) {
      const data = err?.response?.data;
      toast.error(data?.message || data?.error?.message || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: tournament?.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied');
      }
    } catch {
      /* share dismissed */
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 animate-pulse">
        <div className="h-72 bg-slate-300" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="h-40 bg-white rounded-2xl" />
            <div className="h-56 bg-white rounded-2xl" />
          </div>
          <div className="h-80 bg-white rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <AlertCircle className="mx-auto h-12 w-12 text-slate-300 mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Tournament not found</h2>
          <p className="text-slate-600 mb-6">{error || 'It may have been removed.'}</p>
          <Link
            to="/tournaments"
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-medium hover:bg-indigo-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to tournaments
          </Link>
        </div>
      </div>
    );
  }

  const isCancelled = tournament.status === 'cancelled';
  const isFull = tournament.availableSpots <= 0;
  const canRegister = tournament.registrationOpen && !isCancelled && tournament.status === 'upcoming';
  const fillPct = Math.min(
    100,
    Math.round((tournament.participantCount / Math.max(tournament.maxParticipants, 1)) * 100)
  );
  const countdown = tournament.status === 'upcoming' ? countdownLabel(tournament.date) : null;

  let ctaLabel = 'Register now';
  if (registering) ctaLabel = 'Registering…';
  else if (alreadyRegistered) ctaLabel = "You're registered";
  else if (isCancelled) ctaLabel = 'Tournament cancelled';
  else if (tournament.status !== 'upcoming') ctaLabel = 'Registration closed';
  else if (isFull) ctaLabel = 'Tournament full';
  else if (!tournament.registrationOpen) ctaLabel = 'Registration closed';
  else if (!user) ctaLabel = 'Log in to register';

  const ctaDisabled = registering || alreadyRegistered || !canRegister;

  const facts = [
    {
      icon: Calendar,
      label: 'Date',
      value: safeFormat(tournament.date, 'EEE, MMM d, yyyy'),
      sub: tournament.endDate ? `Ends ${safeFormat(tournament.endDate, 'MMM d')}` : tournament.time,
    },
    {
      icon: MapPin,
      label: 'Location',
      value: tournament.location,
      sub: tournament.venue,
    },
    {
      icon: Swords,
      label: 'Format',
      value: formatLabel(tournament.format) || '—',
    },
    {
      icon: Users,
      label: 'Entry fee',
      value: formatFee(tournament.registrationFee),
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-24 lg:pb-0">
      {/* Hero */}
      <div className={`relative bg-gradient-to-br ${sportGradient(tournament.sport)}`}>
        {tournament.bannerImage && (
          <img
            src={tournament.bannerImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/50 to-slate-950/20" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-10 sm:pb-14 min-h-[18rem] sm:min-h-[22rem] flex flex-col">
          <div className="flex items-center justify-between">
            <Link
              to="/tournaments"
              className="inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white bg-white/10 hover:bg-white/20 backdrop-blur rounded-full px-3.5 py-1.5 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> All tournaments
            </Link>
            <button
              onClick={handleShare}
              aria-label="Share tournament"
              className="inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white bg-white/10 hover:bg-white/20 backdrop-blur rounded-full px-3.5 py-1.5 transition-colors"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>

          <div className="mt-auto pt-12 text-white">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs font-semibold uppercase tracking-widest bg-white/15 backdrop-blur rounded-full px-3 py-1">
                {capitalize(tournament.sport)}
              </span>
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ring-inset ${statusStyle(
                  tournament.status
                )}`}
              >
                {capitalize(tournament.status)}
              </span>
              {countdown && (
                <span className="text-xs font-semibold bg-indigo-500 rounded-full px-2.5 py-1">
                  {countdown}
                </span>
              )}
            </div>
            <h1 className="text-3xl sm:text-5xl font-bold tracking-tight max-w-4xl">
              {tournament.title}
            </h1>
            {tournament.organizer && (
              <p className="mt-2 text-white/80">Organised by {tournament.organizer}</p>
            )}
          </div>
        </div>
      </div>

      {/* Key facts */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-md grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-slate-100">
          {facts.map(({ icon: Icon, label, value, sub }) => (
            <div key={label} className="p-4 sm:p-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
                <Icon className="w-3.5 h-3.5" /> {label}
              </div>
              <p className="mt-1.5 font-semibold text-slate-900 leading-snug">{value}</p>
              {sub && <p className="text-sm text-slate-500 mt-0.5">{sub}</p>}
            </div>
          ))}
        </div>
      </div>

      {isCancelled && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 rounded-2xl p-4">
            <Ban className="w-5 h-5 mt-0.5 shrink-0" />
            <p className="text-sm">
              <span className="font-semibold">This tournament has been cancelled.</span> Registration is closed.
            </p>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main */}
          <div className="lg:col-span-2 space-y-6">
            <section className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-3">About this tournament</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">{tournament.description}</p>
            </section>

            {tournament.rules && tournament.rules.length > 0 && (
              <section className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-4">Rules</h2>
                <ul className="space-y-3">
                  {tournament.rules.map((rule, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                      <span className="text-slate-700">{rule}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {(tournament.prizePool || (tournament.prizes && tournament.prizes.length > 0)) && (
              <section className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-4">Prizes</h2>
                {tournament.prizePool && (
                  <div className="flex items-center gap-3 rounded-xl bg-amber-50 ring-1 ring-inset ring-amber-200 p-4 mb-4">
                    <Trophy className="w-6 h-6 text-amber-600" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-amber-700">Prize pool</p>
                      <p className="font-bold text-amber-900">{tournament.prizePool}</p>
                    </div>
                  </div>
                )}
                {tournament.prizes && tournament.prizes.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {tournament.prizes.map((prize, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 rounded-xl border border-slate-200"
                      >
                        <span className="font-medium text-slate-900">{prize.position}</span>
                        <span className="font-semibold text-slate-700">{prize.prize}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {tournament.brochure && (
              <a
                href={tournament.brochure}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200 p-5 hover:border-indigo-300 hover:shadow-md transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <FileText className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">Tournament brochure</p>
                  <p className="text-sm text-slate-500">Open the full details from the organiser</p>
                </div>
                <ArrowLeft className="w-4 h-4 text-slate-400 rotate-180" />
              </a>
            )}

            {tournament.createdBy && (
              <section className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-4">Posted by</h2>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center overflow-hidden">
                    {tournament.createdBy.avatar ? (
                      <img
                        src={tournament.createdBy.avatar}
                        alt={tournament.createdBy.name}
                        className="w-12 h-12 object-cover"
                      />
                    ) : (
                      <User className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{tournament.createdBy.name}</p>
                    <p className="text-sm text-slate-500">Tournament organiser</p>
                  </div>
                </div>
              </section>
            )}
          </div>

          {/* Registration sidebar */}
          <aside className="lg:sticky lg:top-6 self-start">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4">Registration</h3>

              <div className="flex items-baseline justify-between mb-5">
                <span className="text-sm text-slate-500">Entry fee</span>
                <span className="text-2xl font-bold text-slate-900">{formatFee(tournament.registrationFee)}</span>
              </div>

              <div className="mb-5">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">
                    {tournament.participantCount}/{tournament.maxParticipants} registered
                  </span>
                  <span className={isFull ? 'font-semibold text-red-600' : 'text-slate-500'}>
                    {isFull ? 'Full' : `${tournament.availableSpots} left`}
                  </span>
                </div>
                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${isFull ? 'bg-red-500' : 'bg-indigo-500'}`}
                    style={{ width: `${fillPct}%` }}
                  />
                </div>
              </div>

              <div
                className={`flex items-center gap-2 text-sm font-medium mb-1 ${
                  canRegister ? 'text-emerald-600' : 'text-red-600'
                }`}
              >
                {canRegister ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                {canRegister ? 'Registration open' : 'Registration closed'}
              </div>
              {tournament.registrationDeadline && (
                <p className="text-sm text-slate-500 mb-5 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Closes {safeFormat(tournament.registrationDeadline, 'MMM d, yyyy')}
                </p>
              )}

              <button
                disabled={ctaDisabled}
                onClick={handleRegister}
                className={`w-full py-3 px-4 rounded-xl font-semibold transition-colors ${
                  alreadyRegistered
                    ? 'bg-emerald-600 text-white cursor-default'
                    : canRegister
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60'
                    : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                }`}
              >
                {alreadyRegistered && <CheckCircle2 className="inline w-5 h-5 mr-2 -mt-0.5" />}
                {ctaLabel}
              </button>
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile sticky CTA */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3 flex items-center gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Entry</p>
          <p className="text-lg font-bold text-slate-900 leading-none">{formatFee(tournament.registrationFee)}</p>
        </div>
        <button
          disabled={ctaDisabled}
          onClick={handleRegister}
          className={`flex-1 py-3 rounded-xl font-semibold transition-colors ${
            alreadyRegistered
              ? 'bg-emerald-600 text-white'
              : canRegister
              ? 'bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60'
              : 'bg-slate-200 text-slate-500'
          }`}
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
};

export default TournamentDetailPage;
